import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import { modifyTitle } from "./analyticsUtils";
import moment from "moment";
import html2canvas from "html2canvas";

export const handleCSVDownload = (
  rows: any,
  columns: any,
  pageTitle: string,
  facilityName?: string,
  startDate?: Date | string,
  endDate?: Date | string
) => {
  document.title = modifyTitle(pageTitle);

  const csvData = rows.map((row: any) =>
    columns.map((column: any) => {
      if (isDate(row[column.accessor])) {
        row[column.accessor] = moment(row[column.accessor]).format(
          "MM/DD/YYYY"
        );
      } else if (isTime(row[column.accessor])) {
        row[column.accessor] = moment(row[column.accessor], "HH:mm[Z]").format(
          "HH:mm"
        );
      }
      return row[column.accessor];
    })
  );

  const csvHeaders = columns.map((column: any) => column.Header);
  
  // Build CSV content with optional metadata rows
  const metadataRows = [];
  if (facilityName) {
    metadataRows.push(`Facility: ${facilityName}`);
  }
  if (startDate && endDate) {
    metadataRows.push(`Date Range: ${moment(startDate).format("MM-DD-YYYY")} - ${moment(endDate).format("MM-DD-YYYY")}`);
  }
  
  // Add blank line after metadata if metadata exists
  const headerSection = metadataRows.length > 0 
    ? [...metadataRows, "", csvHeaders.join(",")]
    : [csvHeaders.join(",")];
  
  const dataSection = csvData.map((row: any) => row.join(","));
  
  const csvContent = [...headerSection, ...dataSection].join("\n");

  const csvBlob = new Blob([csvContent], {
    type: "text/csv;charset=utf-8",
  });

  saveAs(csvBlob, `${pageTitle}.csv`);
};

export const handlePDFDownload = ({
  data,
  columnData,
  fileName,
  startDate,
  endDate,
  reportType,
  repeatIncidentType,
  subTitle,
}: any) => {
  const filteredColumnData = columnData.filter(
    (col: any) => col.Header !== "View" && col.accessor !== ""
  );

  const pdfHeader = filteredColumnData.map((col: any) => col.Header);
  const pdfAccessor = filteredColumnData.map((col: any) => col.accessor);

  const transformedData = data.map((item: any, index: string) => {
    const safeItem = { ...item };
    if (safeItem.incidentType === null) safeItem.incidentType = "Total";
    const transformedItem = [];
    for (let key of pdfAccessor) {
      let value = safeItem[key] || "-";

      if (isDate(value)) {
        value = moment(value).format("MM/DD/YYYY");
      } else if (isTime(value)) {
        value = moment(value, "HH:mm[Z]").format("HH:mm");
      }

      transformedItem.push(value);
    }
    return transformedItem;
  });

  const doc = new jsPDF({
    orientation: "landscape",
  });

  autoTable(doc, {
    margin: { top: 50 },
    head: [pdfHeader],
    body: transformedData,
    didDrawPage: () => {
      doc.setFontSize(18);
      doc.setTextColor(181, 128, 255);
      doc.text(modifyTitle(fileName), 15, 20);

      if (reportType) {
        doc.setFontSize(16);
        doc.setTextColor(181, 128, 255);
        doc.text(
          `${modifyTitle(reportType)} ${
            repeatIncidentType?.length ? `- ${repeatIncidentType}` : ""
          }`,
          15,
          27
        );
      }

      if (subTitle) {
        doc.setFontSize(16);
        doc.setTextColor(181, 128, 255);
        doc.text(subTitle, 15, 27);
      }

      doc.setFontSize(14);
      doc.setTextColor(181, 128, 255);
      doc.text(
        `${moment(startDate).format("ll")} - ${moment(endDate).format("ll")}`,
        15,
        34
      );
    },
    styles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
  });

  const saveFileName = subTitle ? fileName + "-" + subTitle : fileName;
  doc.save(saveFileName);
};

interface TrendPDFDownloadParams {
  data: Array<Record<string, any>>;
  columnData: Array<{ Header: string; accessor: string }>;
  fileName: string;
  startDate: Date | string;
  endDate: Date | string;
  chartID?: string;
  facilityName?: string;
}

// PDF layout constants for trend reports
const TREND_PDF_MARGIN = 10;
const TREND_PDF_HEADER_SPACE = 35;
const CHART_SCALE = 2;

/**
 * Exports trend report data to PDF with optimized column widths and optional chart visualization
 * @param data - Array of incident trend data records
 * @param columnData - Array of column definitions with Header and accessor properties
 * @param fileName - Name for the exported PDF file
 * @param startDate - Report start date for header display
 * @param endDate - Report end date for header display
 * @param chartID - Optional DOM element ID for chart to include in export
 * @param facilityName - Optional facility name to display in header
 * @returns Promise that resolves when PDF is generated and download initiated
 */
export const handleTrendPDFDownload = async ({
  data,
  columnData,
  fileName,
  startDate,
  endDate,
  chartID,
  facilityName,
}: TrendPDFDownloadParams) => {
  const filteredColumnData = columnData.filter(
    (col) => col.Header !== "View" && col.accessor !== ""
  );

  const pdfHeader = filteredColumnData.map((col) => col.Header);
  const pdfAccessor = filteredColumnData.map((col) => col.accessor);

  const transformedData = data.map((item) => {
    const safeItem = { ...item };
    if (safeItem.incidentType === null) safeItem.incidentType = "Total";
    const transformedItem = [];
    for (let key of pdfAccessor) {
      let value = safeItem[key] || "-";

      if (isDate(value)) {
        value = moment(value).format("MM/DD/YYYY");
      } else if (isTime(value)) {
        value = moment(value, "HH:mm[Z]").format("HH:mm");
      }

      transformedItem.push(value);
    }
    return transformedItem;
  });

  const doc = new jsPDF({
    orientation: "landscape",
    format: "a4",
  });

  autoTable(doc, {
    margin: { top: 50, left: 10, right: 10 },
    head: [pdfHeader],
    body: transformedData,
    didDrawPage: () => {
      doc.setFontSize(18);
      doc.setTextColor(181, 128, 255);
      doc.text(modifyTitle(fileName), 15, 20);

      // Display facility name if provided
      if (facilityName) {
        doc.setFontSize(14);
        doc.setTextColor(181, 128, 255);
        doc.text(`Facility: ${facilityName}`, 15, 27);
      }
    },
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
      overflow: "linebreak",
      cellWidth: "wrap",
    },
    headStyles: {
      fontSize: 9,
      fontStyle: "bold",
      fillColor: [181, 128, 255],
      textColor: [255, 255, 255],
      halign: "center",
    },
    columnStyles: {
      0: { cellWidth: "auto", minCellWidth: 20 }, // First column (incident type)
    },
    tableWidth: "auto",
    horizontalPageBreak: true,
    horizontalPageBreakRepeat: 0,
  });

  // Add chart export if chartID is provided
  if (chartID) {
    try {
      const chartElement = document.getElementById(chartID);
      if (!chartElement) {
        console.warn(`Chart element with ID "${chartID}" not found. Skipping chart export.`);
        doc.save(`${fileName}.pdf`);
        return;
      }

      const canvas = await html2canvas(chartElement, { scale: CHART_SCALE });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const imgProps = doc.getImageProperties(imgData);

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();
      
      let imgWidth = pdfWidth - (TREND_PDF_MARGIN * 2);
      let imgHeight = (imgProps.height * imgWidth) / imgProps.width;

      // If height exceeds available space, scale down to fit
      const maxHeight = pdfHeight - TREND_PDF_HEADER_SPACE - TREND_PDF_MARGIN;
      if (imgHeight > maxHeight) {
        imgHeight = maxHeight;
        imgWidth = (imgProps.width * imgHeight) / imgProps.height;
      }

      doc.addPage();
      doc.setFontSize(18);
      doc.setTextColor(181, 128, 255);
      doc.text("Incident Trend Charts", TREND_PDF_MARGIN, 15);

      doc.setFontSize(14);
      doc.setTextColor(181, 128, 255);
      doc.text(
        `${moment(startDate).format("MM-DD-YYYY")} - ${moment(endDate).format("MM-DD-YYYY")}`,
        TREND_PDF_MARGIN,
        25
      );

      // Center the image horizontally if it doesn't fill the width
      const xPosition = (pdfWidth - imgWidth) / 2;
      doc.addImage(imgData, "JPEG", xPosition, TREND_PDF_HEADER_SPACE, imgWidth, imgHeight);
    } catch (error) {
      console.error("Error rendering chart to PDF:", error);
      // Continue with PDF generation even if chart fails
    }
  }

  doc.save(`${fileName}.pdf`);
};

export const isDate = (value: any): boolean => {
  return moment(value, "YYYY-MM-DD", true).isValid();
};

export const isTime = (value: any): boolean => {
  return moment(value, "HH:mm:ss.sssZ", true).isValid();
};

export const downloadCombinedPDF = async ({
  data,
  columnData,
  fileName,
  startDate,
  endDate,
  reportType,
  repeatIncidentType,
  subTitle,
  chartID,
}: any) => {
  const filteredColumnData = columnData.filter(
    (col: any) => col.Header !== "View" && col.accessor !== ""
  );

  const dateTitle = `${moment(startDate).format("ll")} - ${moment(endDate).format("ll")}`;

  const pdfHeader = filteredColumnData.map((col: any) => col.Header);
  const pdfAccessor = filteredColumnData.map((col: any) => col.accessor);

  const transformedData = data.map((item: any) => {
    const safeItem = { ...item };
    if (safeItem.incidentType === null) safeItem.incidentType = "Total";
    return pdfAccessor.map((key: any) => {
      let value = safeItem[key] || "-";
      if (isDate(value)) {
        value = moment(value).format("MM/DD/YYYY");
      } else if (isTime(value)) {
        value = moment(value, "HH:mm[Z]").format("HH:mm");
      }
      return value;
    });
  });

  const doc = new jsPDF({
    orientation: "landscape",
  });

  // Add table data
  autoTable(doc, {
    margin: { top: 50 },
    head: [pdfHeader],
    body: transformedData,
    didDrawPage: () => {
      doc.setFontSize(18);
      doc.setTextColor(181, 128, 255);
      doc.text(modifyTitle(`${fileName} Trend Report`), 15, 20);

      if (reportType) {
        const repeatIncidentText = repeatIncidentType?.length
          ? `- ${repeatIncidentType}`
          : "";
        doc.setFontSize(16);
        doc.setTextColor(181, 128, 255);
        doc.text(`${modifyTitle(reportType)} ${repeatIncidentText}`, 15, 27);
      }

      if (subTitle) {
        doc.setFontSize(16);
        doc.setTextColor(181, 128, 255);
        doc.text(subTitle, 15, 34);
      }

      doc.setFontSize(14);
      doc.setTextColor(181, 128, 255);
      doc.text(dateTitle, 15, 41);
    },
    styles: {
      lineColor: [0, 0, 0],
      lineWidth: 0.1,
    },
  });

  // Add chart export
  if (chartID) {
    const chartElement = document.getElementById(chartID);
    if (chartElement) {
      const canvas = await html2canvas(chartElement, { scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 1.0);
      const imgProps = doc.getImageProperties(imgData);

      const pdfWidth = doc.internal.pageSize.getWidth();
      const pdfHeight = doc.internal.pageSize.getHeight();

      let imgWidth = pdfWidth;
      let imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (imgHeight > pdfHeight - 100) {
        imgHeight = pdfHeight;
        imgWidth = (imgProps.width * imgHeight) / imgProps.height;
      }

      doc.addPage();
      doc.setFontSize(18);
      doc.setTextColor(181, 128, 255);
      doc.text(`${fileName} Trend Chart`, 15, 27);

      doc.setFontSize(16);
      doc.setTextColor(181, 128, 255);
      doc.text(dateTitle, 15, 37);

      doc.addImage(imgData, "JPEG", 15, 50, imgWidth, imgHeight);
    } else {
      console.error(`Element with ID "${chartID}" not found.`);
    }
  }

  const saveFileName = subTitle ? `${fileName}-${subTitle}` : fileName;
  doc.save(`${saveFileName}.pdf`);
};

